'use client';
import React from 'react';
import Link from 'next/link';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/components/PublicFooter';
import { FileText, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-navy-950 flex flex-col">
      <PublicTopbar />
      <main className="flex-1 pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back */}
          <Link href="/home" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand-500/15 border border-brand-500/25 flex items-center justify-center">
              <FileText size={20} className="text-brand-400" />
            </div>
            <span className="chip">Legal</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-800 text-white mt-3 mb-2">
            Terms of <span className="text-gradient">Service</span>
          </h1>
          <p className="text-slate-400 text-sm mb-10">Last updated: May 11, 2026</p>

          <div className="prose-legal space-y-8 text-slate-300 text-sm leading-relaxed">

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">1. Acceptance of Terms</h2>
              <p>By accessing or using ZipAdBook ("the Service"), operated by Take a Zip LLC ("Company," "we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">2. Description of Service</h2>
              <p>ZipAdBook is a hyperlocal advertising platform that distributes coupon books to businesses across Florida and Puerto Rico. Advertisers may purchase placements, submit artwork, and track campaign delivery through our online platform.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">3. Account Registration</h2>
              <p className="mb-2">To use certain features of the Service, you must create an account. You agree to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Provide accurate, current, and complete information during registration.</li>
                <li>Maintain and promptly update your account information.</li>
                <li>Keep your password secure and confidential.</li>
                <li>Notify us immediately of any unauthorized use of your account.</li>
                <li>Accept responsibility for all activities that occur under your account.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">4. Advertising Placements & Orders</h2>
              <p className="mb-2">When you book an advertising placement:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Orders are subject to availability and confirmation by ZipAdBook.</li>
                <li>You are responsible for providing print-ready artwork that meets our specifications.</li>
                <li>Artwork must be submitted by the stated deadline; late submissions may result in your ad being excluded from the current issue.</li>
                <li>ZipAdBook reserves the right to reject artwork that violates our content guidelines, is offensive, or is legally problematic.</li>
                <li>Distribution quantities are estimates; actual delivery may vary by ±10%.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">5. Payment Terms</h2>
              <p className="mb-2">All payments are processed securely through Stripe. By submitting payment, you agree that:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>All fees are due at the time of booking unless otherwise agreed in writing.</li>
                <li>Prices are listed in USD and are subject to change without notice.</li>
                <li>Bundle pricing discounts apply only when all issues in the bundle are paid in full.</li>
                <li>You authorize us to charge your payment method for the total amount shown at checkout.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">6. Refund Policy</h2>
              <p className="mb-2">All sales are final. <strong className="text-white">No cash refunds are issued.</strong> However:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>If you cancel before artwork approval, a credit equal to the amount paid will be applied to your account for use on a future booking.</li>
                <li>Once artwork has been approved and sent to print, no cancellations or credits can be issued.</li>
                <li>In the event of a printing or distribution error caused by ZipAdBook, we will offer a complimentary re-run in a future issue.</li>
                <li>Credits expire 12 months from the date of issuance.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">7. Intellectual Property</h2>
              <p className="mb-2">You retain ownership of any artwork or content you submit. By submitting content, you grant ZipAdBook a non-exclusive, royalty-free license to reproduce and distribute your content solely for the purpose of fulfilling your advertising order. You represent and warrant that:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>You own or have the right to use all content you submit.</li>
                <li>Your content does not infringe any third-party intellectual property rights.</li>
                <li>Your content complies with all applicable laws and regulations.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">8. Prohibited Content</h2>
              <p className="mb-2">You may not submit or promote content that:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Is false, misleading, or deceptive.</li>
                <li>Is obscene, offensive, or inappropriate for a general business audience.</li>
                <li>Promotes illegal products, services, or activities.</li>
                <li>Infringes on any patent, trademark, trade secret, copyright, or other proprietary rights.</li>
                <li>Contains malware, viruses, or other harmful code.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">9. Limitation of Liability</h2>
              <p>To the maximum extent permitted by law, ZipAdBook and its affiliates, officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of the Service. Our total liability to you for any claim shall not exceed the amount you paid us in the 90 days preceding the claim.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">10. Disclaimer of Warranties</h2>
              <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">11. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Hillsborough County, Florida.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">12. Changes to Terms</h2>
              <p>We reserve the right to modify these Terms at any time. We will notify registered users by email or by posting a notice on the platform. Continued use of the Service after changes constitutes acceptance of the new Terms.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">13. Contact Us</h2>
              <p>If you have questions about these Terms, please contact us:</p>
              <div className="mt-3 p-4 rounded-xl bg-navy-800 border border-white/8">
                <p className="text-white font-medium">Take a Zip LLC / ZipAdBook</p>
                <p className="mt-1">Email: <a href="mailto:legal@zipadbook.com" className="text-brand-400 hover:text-brand-300 transition-colors">legal@zipadbook.com</a></p>
                <p>Phone: <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300 transition-colors">+1 (321) 265-3177</a></p>
                <p>Coverage: Florida & Puerto Rico</p>
              </div>
            </section>

          </div>

          <div className="mt-12 pt-8 border-t border-white/8 flex flex-col sm:flex-row gap-4">
            <Link href="/privacy-policy" className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors">
              View Privacy Policy →
            </Link>
            <Link href="/customer-support" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
              Contact Support →
            </Link>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
